import React, { useEffect, useState } from 'react';
import { PartidaLite } from '../../services/faseGrupoPartidasService';
import { PartidaInput } from '../../services/partidaService';
import { Local } from '../../types/local';
import { Equipe } from '../../types/equipe';
import TeamSelectorDialog from './TeamSelectorDialog';
import { dispatchGlobalError } from '../../context/ApiErrorContext';
import { Tooltip, TextField } from '@mui/material';
import { criarLocal, atualizarLocal } from '../../services/localService';
import { normalizeSets, SetPlacar } from '../../types/placar';
import { formatMatchResult } from '../../utils/matchResultFormatter';
import { BeachTennisScoreValidator } from '../../utils/beachTennisScoreValidator';
import LocalizacaoMapaEditor from '../local/LocalizacaoMapaEditor';

type PartidaLiteEditable = Omit<PartidaLite, 'equipeA' | 'equipeB' | 'data'> & {
  data?: string | null;
  equipeA?: PartidaLite['equipeA'] | null;
  equipeB?: PartidaLite['equipeB'] | null;
};

export type PartidaCardEditorProps = {
  partida: PartidaLiteEditable;
  index?: number;
  localTorneio: Local;
  onEdit?: (partidaId: string) => void;
  onDelete?: (partidaId: string) => void;
  onSave?: (p: Partial<PartidaInput> & { id: string }) => void;
  equipes: Equipe[];
  loadingEquipes?: boolean;
  onCarregarEquipes?: () => Promise<void>;
  initiallyEditing?: boolean;
  onCancel?: () => void;
  saving?: boolean;
  gamesPorSets?: number | null;
  tiebreaks?: number | null;
  numeroSets?: number | null;
  categoriaTorneio?: {
    gamesPorSets?: number | null;
    tiebreaks?: number | null;
    numeroSets?: number | null;
  } | null;
};
type FormState = {
  data: string;
  localId: string;
  resultadoFinalA: string;
  resultadoFinalB: string;
  sets?: ReturnType<typeof normalizeSets>;
};

const PartidaCardEditor: React.FC<PartidaCardEditorProps> = ({
  partida,
  index,
  localTorneio,
  onDelete,
  onSave,
  equipes,
  loadingEquipes = false,
  onCarregarEquipes,
  initiallyEditing = false,
  onCancel,
  saving = false,
  gamesPorSets,
  tiebreaks,
  numeroSets,
  categoriaTorneio,
}) => {
  const [editing, setEditing] = useState(initiallyEditing);
  const [form, setForm] = useState<FormState>({
    data: partida.data || '',
    localId: partida.local?.id || '',
    resultadoFinalA:
      partida.placar?.resultadoFinalA !== undefined &&
      partida.placar?.resultadoFinalA !== null
        ? String(partida.placar.resultadoFinalA)
        : '',
    resultadoFinalB:
      partida.placar?.resultadoFinalB !== undefined &&
      partida.placar?.resultadoFinalB !== null
        ? String(partida.placar.resultadoFinalB)
        : '',
    sets: normalizeSets(partida.placar?.sets),
  });

  const [equipeA, setEquipeA] = useState<Equipe | null>(null);
  const [equipeB, setEquipeB] = useState<Equipe | null>(null);
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [selectorTarget, setSelectorTarget] = useState<'A' | 'B' | null>(null);
  const [busca, setBusca] = useState('');
  const [localNome, setLocalNome] = useState('');
  const [localUrlLocalizacao, setLocalUrlLocalizacao] = useState('');
  const [novoLocalId, setNovoLocalId] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [equipeAWO, setEquipeAWO] = useState(false);
  const [equipeBWO, setEquipeBWO] = useState(false);
  const [equipeADesistencia, setEquipeADesistencia] = useState(false);
  const [equipeBDesistencia, setEquipeBDesistencia] = useState(false);
  const isSaving = salvando || saving;

  const normalizedSets = form.sets ?? normalizeSets(partida.placar?.sets);

  useEffect(() => {
    setEquipeA(partida.equipeA || null);
    setEquipeB(partida.equipeB || null);
    setEquipeAWO(partida.equipeAWO || false);
    setEquipeBWO(partida.equipeBWO || false);
    setEquipeADesistencia(partida.equipeADesistencia || false);
    setEquipeBDesistencia(partida.equipeBDesistencia || false);

    setForm({
      data: partida.data || '',
      localId: partida.local?.id || '',
      resultadoFinalA:
        partida.placar?.resultadoFinalA !== undefined &&
        partida.placar?.resultadoFinalA !== null
          ? String(partida.placar.resultadoFinalA)
          : '',
      resultadoFinalB:
        partida.placar?.resultadoFinalB !== undefined &&
        partida.placar?.resultadoFinalB !== null
          ? String(partida.placar.resultadoFinalB)
          : '',
      sets: normalizeSets(partida.placar?.sets),
    });
    setLocalNome('');
    setNovoLocalId(null);
    setLocalUrlLocalizacao(partida.local?.urlLocalizacao ?? '');
    setEditing(initiallyEditing);
  }, [partida, initiallyEditing]);

  const numero = partida as any;
  const partidaNumero =
    (numero && (numero as any).numero) || index ? (index || 0) + 1 : 1;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: name === 'resultadoFinalA' || name === 'resultadoFinalB'
        ? value.replace(/[^0-9]/g, '')
        : value
    }));
  };

  const handleSetChange = (
    numeroSet: number,
    campo: 'gamesA' | 'gamesB' | 'tiebreakA' | 'tiebreakB',
    value: string,
  ) => {
    const sanitized = value.replace(/[^0-9]/g, '');
    setForm(prev => {
      const currentSets = prev.sets ?? normalizeSets(partida.placar?.sets);
      const sets = [...currentSets];
      const index = numeroSet - 1;
      const current = sets[index];
      if (!current) {
        return prev;
      }
      
      const updatedSet: SetPlacar = {
        ...current,
        [campo]: sanitized === '' ? null : Number(sanitized),
      };
      
      // Se estamos atualizando gamesA ou gamesB, verificar se o set ainda precisa de tiebreak
      // Se não precisar, limpar tiebreakA e tiebreakB
      if (campo === 'gamesA' || campo === 'gamesB') {
        const finalGamesPorSets = gamesPorSets ?? categoriaTorneio?.gamesPorSets ?? null;
        const finalTiebreaks = tiebreaks ?? categoriaTorneio?.tiebreaks ?? null;
        
        const setDeveTerTiebreak = BeachTennisScoreValidator.deveTerTiebreak(
          updatedSet,
          finalGamesPorSets,
          finalTiebreaks
        );
        
        if (!setDeveTerTiebreak) {
          updatedSet.tiebreakA = null;
          updatedSet.tiebreakB = null;
        }
      }
      
      sets[index] = updatedSet;
      return { ...prev, sets };
    });
  };
  
  // Função para verificar se um set deve ter tiebreak preenchido
  const deveTerTiebreak = (set: SetPlacar): boolean => {
    const finalGamesPorSets = gamesPorSets ?? categoriaTorneio?.gamesPorSets ?? null;
    const finalTiebreaks = tiebreaks ?? categoriaTorneio?.tiebreaks ?? null;
    
    return BeachTennisScoreValidator.deveTerTiebreak(set, finalGamesPorSets, finalTiebreaks);
  };

  

  const openSelector = async (target: 'A' | 'B') => {
    if (loadingEquipes || !editing) return;
    setSelectorTarget(target);
    setIsSelectorOpen(true);

    // lazy load, se o pai expôs onCarregarEquipes e não há equipes ainda
    if (equipes.length === 0 && onCarregarEquipes) {
      try {
        await onCarregarEquipes();
      } catch {
        dispatchGlobalError('Erro ao carregar equipes.');
      }
    }
  };

  const handleSelectEquipe = (eq: Equipe) => {
    let novaEquipeA = equipeA;
    let novaEquipeB = equipeB;
    if (selectorTarget === 'A') {
      setEquipeA(eq);
      novaEquipeA = eq;
    } else if (selectorTarget === 'B') {
      setEquipeB(eq);
      novaEquipeB = eq;
    }
    setIsSelectorOpen(false);
    setSelectorTarget(null);
  };

  const handleWOChange = (equipe: 'A' | 'B', checked: boolean) => {
    if (equipe === 'A') {
      setEquipeAWO(checked);
      if (checked) {
        setEquipeADesistencia(false);
      }
    } else {
      setEquipeBWO(checked);
      if (checked) {
        setEquipeBDesistencia(false);
      }
    }
  };

  const handleDesistenciaChange = (equipe: 'A' | 'B', checked: boolean) => {
    if (equipe === 'A') {
      setEquipeADesistencia(checked);
      if (checked) {
        setEquipeAWO(false);
      }
    } else {
      setEquipeBDesistencia(checked);
      if (checked) {
        setEquipeBWO(false);
      }
    }
  };

  const handleSave = async () => {
    if (equipeA && equipeB && equipeA.id === equipeB.id) {
      dispatchGlobalError('A mesma equipe não pode ser selecionada para A e B.');
      return;
    }
    setSalvando(true);
    let localId: string | null = form.localId || null;
    const nomeTrim = localNome.trim();
    if (nomeTrim) {
      if (!localTorneio?.endereco?.id) {
        dispatchGlobalError('Endereço do torneio não disponível.');
        setSalvando(false);
        return;
      }
      try {
        if (!novoLocalId) {
          const novoLocal = await criarLocal({
            nome: nomeTrim,
            descricao: '',
            enderecoId: localTorneio.endereco.id,
            urlLocalizacao: localUrlLocalizacao.trim() || null,
          });
          setNovoLocalId(novoLocal.id);
          localId = novoLocal.id;
        } else {
          localId = novoLocalId;
        }
      } catch (e) {
        dispatchGlobalError('Não foi possível criar o local');
        setSalvando(false);
        return;
      }
    } else if (localId && localUrlLocalizacao.trim() !== (partida.local?.urlLocalizacao ?? '')) {
      try {
        await atualizarLocal(localId, { urlLocalizacao: localUrlLocalizacao.trim() || null });
      } catch (e) {
        dispatchGlobalError('Não foi possível atualizar a localização do local');
        setSalvando(false);
        return;
      }
    }
    try {
      const temWO = equipeAWO || equipeBWO;
      await onSave?.({
        id: partida.id,
        data: form.data,
        localId: localId,
        equipeAId: equipeA ? equipeA.id : null,
        equipeBId: equipeB ? equipeB.id : null,
        equipeAWO: equipeAWO || null,
        equipeBWO: equipeBWO || null,
        equipeADesistencia: equipeADesistencia || null,
        equipeBDesistencia: equipeBDesistencia || null,
        placar: temWO ? null : {
          resultadoFinalA:
            form.resultadoFinalA !== '' ? Number(form.resultadoFinalA) : null,
          resultadoFinalB:
            form.resultadoFinalB !== '' ? Number(form.resultadoFinalB) : null,
          sets: normalizedSets,
        },
      });
      setEditing(false);
      setLocalNome('');
      setNovoLocalId(null);
      onCancel?.();
    } catch (e) {
      dispatchGlobalError('Erro ao salvar partida');
    } finally {
      setSalvando(false);
    }
  };

  const handleToggleEditing = () => {
    setEditing(prev => {
      const next = !prev;
      if (!next) {
        onCancel?.();
      }
      return next;
    });
  };

  const handleCancelEdition = () => {
    setEditing(false);
    onCancel?.();
  };

  const renderEquipe = (eq: Equipe | null) => {
    if (!eq) {
      return (
        <div className="d-flex flex-wrap flex-row">
          <div className='d-flex col flex-column align-items-center'>
            <div className='rounded-circle border align-items-center d-flex justify-content-center' style={{ width: 50, height: 50 }}>
              <i className="bi bi-plus-circle-fill"></i>
            </div>
          </div>
        </div>
      )
    }
    return (
      <div>
        <div className="d-flex flex-wrap flex-row">
          {eq.atletas.map(a => (
            <div key={a.id} className='d-flex col flex-column align-items-center'>
              <img
                src={a.usuario?.imagemUrl || '/assets/images/user.png'}
                alt={a.usuario?.nome ?? 'Atleta'}
                className="rounded-circle me-1 mb-1"
                width={32}
                height={32}
                loading="lazy"
                style={{ objectFit: 'contain', maxWidth: 'fit-content' }}
              />
              {a.usuario?.nome ?? 'Atleta'}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="card mb-3" data-testid={`partida-card-${partida.id}`}>
      <div className="card-header d-flex justify-content-between align-items-center">
        <span>Partida #{partidaNumero}</span>
        <div>
          <button
            type="button"
            className="btn btn-link p-0 me-2"
            aria-label="Editar partida"
            data-testid={`btn-editar-partida-${partida.id}`}
            onClick={handleToggleEditing}
          >
            <Tooltip title="Editar partida">
              <i className="bi bi-pencil" />
            </Tooltip>
          </button>
          <button
            type="button"
            className="btn btn-link p-0"
            aria-label="Excluir partida"
            data-testid={`btn-deletar-partida-${partida.id}`}
            onClick={() => onDelete?.(partida.id)}
          >
            <i className="bi bi-trash" />
          </button>
        </div>
      </div>
      <div className="card-body">
        <div className="d-flex">
          <div className="row flex-grow-1">
            <div
              className="col-6 mb-2"
              id="equipe-a"
              role="button"
              style={{ cursor: editing ? 'pointer' : 'default' }}
              onClick={() => openSelector('A')}
            >
              {renderEquipe(equipeA)}
            </div>
            <div
              className="col-6"
              id="equipe-b"
              role="button"
              style={{ cursor: editing ? 'pointer' : 'default' }}
              onClick={() => openSelector('B')}
            >
              {renderEquipe(equipeB)}
            </div>
          </div>
          <div className="d-flex align-items-center" style={{ minWidth: 80 }}>
            {editing ? null : (
              <strong>
                {formatMatchResult(partida)}
              </strong>
            )}
          </div>
        </div>
        <div className="d-flex justify-content-between mt-2">
          {editing ? (
            <>
              <input
                type="datetime-local"
                name="data"
                value={form.data}
                onChange={handleChange}
                className="form-control me-2"
                style={{ maxWidth: '50%' }}
              />
              <TextField
                label="Local da partida"
                placeholder="Digite o nome do local"
                value={localNome}
                onChange={e => setLocalNome(e.target.value)}
                size="small"
                className="flex-grow-1"
                inputProps={{ 'data-testid': 'input-local-partida' }}
              />
            </>
          ) : (
            <span>
              {partida.data} - {partida.local?.nome ?? localTorneio?.nome}
            </span>
          )}
        </div>
        {editing && (
          <div className="mt-2">
            <LocalizacaoMapaEditor
              urlLocalizacao={localUrlLocalizacao}
              onChange={setLocalUrlLocalizacao}
            />
          </div>
        )}
        {editing && (
          <div className="mt-3">
            <div className="fw-bold mb-2">WO e Desistência</div>
            <div className="mb-2">
              <div className="d-flex align-items-center mb-2">
                <input
                  type="checkbox"
                  id="equipe-a-wo"
                  checked={equipeAWO}
                  onChange={(e) => handleWOChange('A', e.target.checked)}
                  className="form-check-input me-2"
                />
                <label htmlFor="equipe-a-wo" className="form-check-label">
                  Equipe A - WO
                </label>
              </div>
              <div className="d-flex align-items-center mb-2">
                <input
                  type="checkbox"
                  id="equipe-a-desistencia"
                  checked={equipeADesistencia}
                  onChange={(e) => handleDesistenciaChange('A', e.target.checked)}
                  className="form-check-input me-2"
                />
                <label htmlFor="equipe-a-desistencia" className="form-check-label">
                  Equipe A - Desistência
                </label>
              </div>
              <div className="d-flex align-items-center mb-2">
                <input
                  type="checkbox"
                  id="equipe-b-wo"
                  checked={equipeBWO}
                  onChange={(e) => handleWOChange('B', e.target.checked)}
                  className="form-check-input me-2"
                />
                <label htmlFor="equipe-b-wo" className="form-check-label">
                  Equipe B - WO
                </label>
              </div>
              <div className="d-flex align-items-center">
                <input
                  type="checkbox"
                  id="equipe-b-desistencia"
                  checked={equipeBDesistencia}
                  onChange={(e) => handleDesistenciaChange('B', e.target.checked)}
                  className="form-check-input me-2"
                />
                <label htmlFor="equipe-b-desistencia" className="form-check-label">
                  Equipe B - Desistência
                </label>
              </div>
            </div>
          </div>
        )}
        {editing && !equipeAWO && !equipeBWO && (
          <div className="mt-3">
            <div className="fw-bold mb-2">Resultado final</div>
            <div className="d-flex align-items-center" style={{ minWidth: 80 }}>
              <input
                type="number"
                name="resultadoFinalA"
                value={form.resultadoFinalA}
                onChange={handleChange}
                className="form-control me-1"
                style={{ width: 56 }}
                data-testid="input-resultado-final-a"
              />
              <span className="mx-1">x</span>
              <input
                type="number"
                name="resultadoFinalB"
                value={form.resultadoFinalB}
                onChange={handleChange}
                className="form-control ms-1"
                style={{ width: 56 }}
                data-testid="input-resultado-final-b"
              />
            </div>
          </div>
        )}
        {editing && !equipeAWO && !equipeBWO && (
          <div className="mt-3">
            <div className="fw-bold mb-2">Placar por set</div>
            {[1, 2, 3].map(numeroSet => {
              const set = normalizedSets[numeroSet - 1];
              return (
                <div
                  key={numeroSet}
                  className="d-flex align-items-center mb-2"
                  data-testid={`linha-set-${partida.id}-${numeroSet}`}
                >
                  <div className="me-2" style={{ width: 60 }}>
                    Set {numeroSet}
                  </div>
                  <input
                    type="number"
                    inputMode="numeric"
                    className="form-control me-1"
                    style={{ width: 56 }}
                    value={set?.gamesA ?? ''}
                    onChange={e => handleSetChange(numeroSet, 'gamesA', e.target.value)}
                    data-testid={`set-${partida.id}-${numeroSet}-a`}
                  />
                  <span className="mx-1">x</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    className="form-control ms-1 me-3"
                    style={{ width: 56 }}
                    value={set?.gamesB ?? ''}
                    onChange={e => handleSetChange(numeroSet, 'gamesB', e.target.value)}
                    data-testid={`set-${partida.id}-${numeroSet}-b`}
                  />
                  <span className="me-1">Tiebreak</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    className="form-control me-1"
                    style={{ 
                      width: 56,
                      backgroundColor: !deveTerTiebreak(set) ? '#e9ecef' : undefined,
                    }}
                    disabled={!deveTerTiebreak(set)}
                    value={set?.tiebreakA ?? ''}
                    onChange={e => handleSetChange(numeroSet, 'tiebreakA', e.target.value)}
                    data-testid={`tb-${partida.id}-${numeroSet}-a`}
                  />
                  <input
                    type="number"
                    inputMode="numeric"
                    className="form-control"
                    style={{ 
                      width: 56,
                      backgroundColor: !deveTerTiebreak(set) ? '#e9ecef' : undefined,
                    }}
                    disabled={!deveTerTiebreak(set)}
                    value={set?.tiebreakB ?? ''}
                    onChange={e => handleSetChange(numeroSet, 'tiebreakB', e.target.value)}
                    data-testid={`tb-${partida.id}-${numeroSet}-b`}
                  />
                </div>
              );
            })}
          </div>
        )}
        {editing && (
          <div className="text-end mt-3">
            <button
              className="btn btn-sm btn-secondary me-2"
              onClick={handleCancelEdition}
            >
              Cancelar
            </button>
            <button
              className="btn btn-sm btn-primary"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              ) : (
                'Salvar'
              )}
            </button>
          </div>
        )}
      </div>
      <TeamSelectorDialog
        open={isSelectorOpen}
        equipes={equipes}
        loading={loadingEquipes}
        busca={busca}
        onBuscaChange={setBusca}
        onClose={() => {
          setIsSelectorOpen(false);
          setSelectorTarget(null);
        }}
        onSelect={handleSelectEquipe}
        disabledTeamId={
          selectorTarget === 'A' ? equipeB?.id : equipeA?.id
        }
      />
    </div>
  );
};

export default PartidaCardEditor;

